<script setup lang="ts">
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import type { ZwDatabaseRelationPair } from "../lib/zwConfig";
import { trimmedMaybe } from "../lib/trimmedString";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  entityGraphNodeBodyStyle,
  entityGraphNodeLabelStyle,
} from "../lib/entityColors";
import { displayFileName } from "../lib/fileNames";
import { entityTypeLabel } from "../lib/entityFrontmatter";
import type { EntityLink } from "../lib/entityFrontmatter";
import { newEntityLinkId } from "../lib/entityLinks";
import {
  autoLayoutPositions,
  autoLayoutSceneNodes,
  arcCharacterEntries,
  buildGraph,
  graphNodeSize,
  loadGraphLayout,
  persistGraphLayout,
  sceneNodeLabelLines,
  sceneSourcesFromArcEdges,
  type GraphEdge,
  type GraphLayout,
  type GraphNode,
} from "../lib/entityGraph";
import { databaseEntryLabel } from "../lib/storyboardArcs";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import {
  getProjectDatabaseEntryFields,
  updateProjectDatabaseEntryFields,
} from "../lib/tauri/database";
import EntityRelationshipDialog from "./EntityRelationshipDialog.vue";
import { useEntityGraphPort } from "../composables/useEntityGraphPort";
import ZwCheckbox from "./ui/ZwCheckbox.vue";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuSeparator from "./ui/ZwMenuSeparator.vue";
import { IconFilter } from "./ribbonIcons";

const port = useEntityGraphPort();

const chapters = computed(() => port.chapters);

const layout = ref<GraphLayout>({});
const typeFilters = ref<Set<string>>(new Set());
const tagFilters = ref<Set<string>>(new Set());
const showArcScenes = ref(false);
const arcCharacterFilters = ref<Set<string>>(new Set());

const panX = ref(0);
const panY = ref(0);
const zoom = ref(1);

const draggingNodeId = ref<string | null>(null);
const dragOffset = ref({ x: 0, y: 0 });
let nodePointerDidDrag = false;
const panning = ref(false);
const panStart = ref({ x: 0, y: 0, panX: 0, panY: 0 });

const linkingFromId = ref<string | null>(null);
const linkCursor = ref<{ x: number; y: number } | null>(null);

const selectedEdgeId = ref<string | null>(null);

const svgEl = ref<SVGSVGElement | null>(null);

let layoutLoadToken = 0;
const persistLayoutTimer = createTimeoutHandle();

watch(
  () => fromMaybe(port.rootPath),
  (root) => {
    layoutLoadToken += 1;
    const token = layoutLoadToken;
    if (root === null || root.length === 0) {
      layout.value = {};
      return;
    }
    void loadGraphLayout(root).then((loaded) => {
      if (token === layoutLoadToken) layout.value = loaded;
    });
  },
  { immediate: true },
);

const availableTypes = computed(() => {
  const present = new Set<string>();
  for (const entry of port.databaseEntryFields) {
    if (entry.isMap) continue;
    const typeM = trimmedMaybe(fromMaybe(entry.fields.type));
    if (isJust(typeM)) present.add(typeM.value);
  }
  return [...present].sort();
});

const availableTags = computed(() => {
  const present = new Set<string>();
  for (const entry of port.databaseEntryFields) {
    if (entry.isMap) continue;
    for (const tag of entry.fields.tags) present.add(tag);
  }
  return [...present].sort();
});

const availableArcCharacters = computed(() =>
  arcCharacterEntries(port.databaseEntryFields)
    .map((entry) => ({
      id: entry.name,
      label: databaseEntryLabel(entry),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    ),
);

const hasActiveFilters = computed(
  () =>
    typeFilters.value.size > 0 ||
    tagFilters.value.size > 0 ||
    arcCharacterFilters.value.size > 0,
);

const graph = computed(() =>
  buildGraph(
    port.databaseEntryFields,
    layout.value,
    {
      types: typeFilters.value.size > 0 ? just(typeFilters.value) : nothing,
      tags: tagFilters.value.size > 0 ? just(tagFilters.value) : nothing,
    },
    {
      arc: {
        enabled: showArcScenes.value,
        characters:
          arcCharacterFilters.value.size > 0
            ? just(arcCharacterFilters.value)
            : nothing,
      },
      chapters: chapters.value,
      relationPairs: matchMaybe(port.projectConfig, {
        just: (config): Maybe<readonly ZwDatabaseRelationPair[]> =>
          just(config.databaseRelationPairs),
        nothing: (): Maybe<readonly ZwDatabaseRelationPair[]> => nothing,
      }),
    },
  ),
);

const nodesById = computed(() => {
  const map = new Map<string, GraphNode>();
  for (const node of graph.value.nodes) map.set(node.id, node);
  return map;
});

const transform = computed(
  () => `translate(${panX.value} ${panY.value}) scale(${zoom.value})`,
);

function persistLayoutNow(): void {
  const root = fromMaybe(port.rootPath);
  if (root === null || root.length === 0) return;
  void persistGraphLayout(root, layout.value);
}

function schedulePersistLayout(): void {
  const root = fromMaybe(port.rootPath);
  if (root === null || root.length === 0) return;
  const snapshot = { ...layout.value };
  persistLayoutTimer.schedule(400, () => {
    void persistGraphLayout(root, snapshot);
  });
}

function toggleTypeFilter(type: string): void {
  const next = new Set(typeFilters.value);
  if (next.has(type)) next.delete(type);
  else next.add(type);
  typeFilters.value = next;
}

function toggleTagFilter(tag: string): void {
  const next = new Set(tagFilters.value);
  if (next.has(tag)) next.delete(tag);
  else next.add(tag);
  tagFilters.value = next;
}

function toggleArcCharacterFilter(entryFile: string): void {
  const next = new Set(arcCharacterFilters.value);
  if (next.has(entryFile)) next.delete(entryFile);
  else next.add(entryFile);
  arcCharacterFilters.value = next;
}

function clearFilters(): void {
  typeFilters.value = new Set();
  tagFilters.value = new Set();
  arcCharacterFilters.value = new Set();
}

function reLayout(): void {
  const entryIds = graph.value.nodes
    .filter((node) => node.kind === "entry")
    .map((node) => node.id);
  const scenePaths = graph.value.nodes
    .filter(
      (node) =>
        node.kind === "scene" &&
        typeof node.scenePath === "string" &&
        node.scenePath.length > 0,
    )
    .map((node) => node.scenePath!);
  const entryLayout = autoLayoutPositions(entryIds);
  let next: GraphLayout = { ...layout.value, ...entryLayout };
  if (showArcScenes.value && scenePaths.length > 0) {
    const entryNodes = entryIds.map((id) => ({
      id,
      x: entryLayout[id]?.x ?? 0,
      y: entryLayout[id]?.y ?? 0,
    }));
    next = {
      ...next,
      ...autoLayoutSceneNodes(
        scenePaths,
        chapters.value,
        entryNodes,
        sceneSourcesFromArcEdges(graph.value.edges),
      ),
    };
  }
  layout.value = next;
  persistLayoutNow();
  fitView();
}

function fitView(): void {
  const nodes = graph.value.nodes;
  if (nodes.length === 0 || svgEl.value === null) {
    panX.value = 0;
    panY.value = 0;
    zoom.value = 1;
    return;
  }
  const rect = svgEl.value.getBoundingClientRect();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    const { w, h } = graphNodeSize(node);
    minX = Math.min(minX, node.x - w / 2);
    minY = Math.min(minY, node.y - h / 2);
    maxX = Math.max(maxX, node.x + w / 2);
    maxY = Math.max(maxY, node.y + h / 2);
  }
  const pad = 64;
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  const scale = Math.min(
    1.25,
    Math.max(
      0.35,
      Math.min(
        (rect.width - pad * 2) / width,
        (rect.height - pad * 2) / height,
      ),
    ),
  );
  zoom.value = scale;
  panX.value = rect.width / 2 - ((minX + maxX) / 2) * scale;
  panY.value = rect.height / 2 - ((minY + maxY) / 2) * scale;
}

onMounted(() => {
  requestAnimationFrame(fitView);
});

watch(
  () => graph.value.nodes.length,
  (count, prev) => {
    if (prev === 0 && count > 0) requestAnimationFrame(fitView);
  },
);

function screenToWorld(
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const rect = svgEl.value?.getBoundingClientRect();
  if (typeof rect === "undefined") return { x: 0, y: 0 };
  return {
    x: (clientX - rect.left - panX.value) / zoom.value,
    y: (clientY - rect.top - panY.value) / zoom.value,
  };
}

function onWheel(event: WheelEvent): void {
  event.preventDefault();
  const rect = svgEl.value?.getBoundingClientRect();
  if (typeof rect === "undefined") return;
  const before = screenToWorld(event.clientX, event.clientY);
  const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
  zoom.value = Math.min(2.5, Math.max(0.35, zoom.value * factor));
  const after = {
    x: (event.clientX - rect.left - panX.value) / zoom.value,
    y: (event.clientY - rect.top - panY.value) / zoom.value,
  };
  panX.value += (after.x - before.x) * zoom.value;
  panY.value += (after.y - before.y) * zoom.value;
}

function onBackgroundPointerDown(event: PointerEvent): void {
  if (event.button !== 0 && event.button !== 1) return;
  if ((event.target as Element).closest(".entity-graph-node") !== null) return;
  selectedEdgeId.value = null;
  panning.value = true;
  panStart.value = {
    x: event.clientX,
    y: event.clientY,
    panX: panX.value,
    panY: panY.value,
  };
  (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
}

function onBackgroundPointerMove(event: PointerEvent): void {
  if (panning.value) {
    panX.value = panStart.value.panX + (event.clientX - panStart.value.x);
    panY.value = panStart.value.panY + (event.clientY - panStart.value.y);
  }
  if (linkingFromId.value !== null && linkingFromId.value.length > 0) {
    linkCursor.value = screenToWorld(event.clientX, event.clientY);
  }
  if (draggingNodeId.value !== null && draggingNodeId.value.length > 0) {
    nodePointerDidDrag = true;
    const world = screenToWorld(event.clientX, event.clientY);
    const id = draggingNodeId.value;
    layout.value = {
      ...layout.value,
      [id]: {
        x: world.x - dragOffset.value.x,
        y: world.y - dragOffset.value.y,
      },
    };
  }
}

function onBackgroundPointerUp(event: PointerEvent): void {
  if (draggingNodeId.value !== null && draggingNodeId.value.length > 0) {
    draggingNodeId.value = null;
    schedulePersistLayout();
  }
  panning.value = false;
  if (linkingFromId.value !== null && linkingFromId.value.length > 0) {
    const from = linkingFromId.value;
    const world = screenToWorld(event.clientX, event.clientY);
    linkingFromId.value = null;
    linkCursor.value = null;
    const target = hitTestNode(world.x, world.y, from, { linkTarget: true });
    if (target !== null) openCreateLink(from, target.id);
  }
}

watch(
  () => [showArcScenes.value, graph.value.nodes.length] as const,
  ([show, count], [, prevCount]) => {
    if (show && count > 0 && prevCount !== count) {
      requestAnimationFrame(fitView);
    }
  },
);

function nodeBounds(node: GraphNode): { w: number; h: number } {
  return graphNodeSize(node);
}

function isGraphNodeActive(nodeId: string): boolean {
  return matchMaybe(port.activeDatabase, {
    just: (active) => active === nodeId,
    nothing: () => false,
  });
}

function graphTypeFilterLabel(typeId: string): string {
  return matchMaybe(entityTypeLabel(just(typeId)), {
    just: (label) => label,
    nothing: () => typeId,
  });
}

function graphNodeTypeCaption(type: Maybe<string>): string {
  return matchMaybe(entityTypeLabel(type), {
    just: (label) => label,
    nothing: () =>
      matchMaybe(type, {
        just: (id) => id,
        nothing: () => "",
      }),
  });
}

function nodeBodyStyle(node: GraphNode): Record<string, string> | undefined {
  if (node.kind === "scene") return undefined;
  return matchMaybe(
    entityGraphNodeBodyStyle(node.color, isGraphNodeActive(node.id)),
    {
      nothing: () => undefined,
      just: (style) => style,
    },
  );
}

function nodeLabelFill(node: GraphNode): { fill: string } | undefined {
  if (node.kind === "scene") return undefined;
  return matchMaybe(entityGraphNodeLabelStyle(node.color), {
    nothing: () => undefined,
    just: (styles) => ({ fill: styles.label }),
  });
}

function nodeTypeFill(node: GraphNode): { fill: string } | undefined {
  if (node.kind === "scene") return undefined;
  return matchMaybe(entityGraphNodeLabelStyle(node.color), {
    nothing: () => undefined,
    just: (styles) => ({ fill: styles.type }),
  });
}

function hitTestNode(
  x: number,
  y: number,
  excludeId?: string,
  opts: { linkTarget?: boolean } = {},
): GraphNode | null {
  for (const node of graph.value.nodes) {
    if (
      typeof excludeId === "string" &&
      excludeId.length > 0 &&
      node.id === excludeId
    )
      continue;
    if (opts.linkTarget === true && node.kind === "scene") continue;
    const { w, h } = nodeBounds(node);
    if (
      x >= node.x - w / 2 &&
      x <= node.x + w / 2 &&
      y >= node.y - h / 2 &&
      y <= node.y + h / 2
    ) {
      return node;
    }
  }
  return null;
}

function onNodePointerDown(event: PointerEvent, node: GraphNode): void {
  if (event.button !== 0) return;
  event.stopPropagation();
  panning.value = false;
  selectedEdgeId.value = null;
  nodePointerDidDrag = false;
  const world = screenToWorld(event.clientX, event.clientY);
  draggingNodeId.value = node.id;
  dragOffset.value = { x: world.x - node.x, y: world.y - node.y };
  // Keep click events on the node; capturing on the SVG retargets them away.
  (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
}

function sceneNodePoints(node: GraphNode): string {
  const { w, h } = graphNodeSize(node);
  const cx = w / 2;
  const cy = h / 2;
  const insetX = 10;
  const insetY = 10;
  return `${cx},${insetY} ${w - insetX},${cy} ${cx},${h - insetY} ${insetX},${cy}`;
}

function sceneLabelLineOffset(node: GraphNode, lineIndex: number): number {
  const { h } = graphNodeSize(node);
  const lines = sceneNodeLabelLines(node.label);
  if (lines.length < 2) return h / 2;
  const lineHeight = 13;
  const blockTop = h / 2 - lineHeight / 2;
  return blockTop + lineIndex * lineHeight;
}

function onNodeClick(event: MouseEvent, node: GraphNode): void {
  event.stopPropagation();
  if (nodePointerDidDrag) {
    nodePointerDidDrag = false;
    return;
  }
  if (
    node.kind === "scene" &&
    typeof node.scenePath === "string" &&
    node.scenePath.length > 0
  ) {
    void port.selectProjectFile(node.scenePath);
    return;
  }
  if (event.detail >= 2) {
    port.closeGraphView();
    void port.selectProjectDatabase(node.id);
    return;
  }
  void port.selectProjectDatabase(node.id);
}

function onNodeDblClick(event: MouseEvent, node: GraphNode): void {
  event.stopPropagation();
  if (
    node.kind === "scene" &&
    typeof node.scenePath === "string" &&
    node.scenePath.length > 0
  ) {
    port.closeGraphView();
    void port.selectProjectFile(node.scenePath);
  }
}

function onLinkHandlePointerDown(event: PointerEvent, node: GraphNode): void {
  if (event.button !== 0 || node.kind !== "entry") return;
  event.stopPropagation();
  linkingFromId.value = node.id;
  linkCursor.value = screenToWorld(event.clientX, event.clientY);
  // Capture on the SVG so pointermove/up keep firing while dragging off the handle.
  svgEl.value?.setPointerCapture?.(event.pointerId);
}

function edgePath(edge: GraphEdge): string {
  const source = nodesById.value.get(edge.sourceId);
  const target = nodesById.value.get(edge.targetId);
  if (typeof source === "undefined" || typeof target === "undefined") return "";
  const sourceSize = graphNodeSize(source);
  const targetSize = graphNodeSize(target);
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const distance = Math.hypot(dx, dy);
  const len = distance > 0 ? distance : 1;
  const nx = dx / len;
  const ny = dy / len;
  const startX = source.x + nx * (sourceSize.w / 2);
  const startY = source.y + ny * (sourceSize.h / 2);
  const endX = target.x - nx * (targetSize.w / 2 + 6);
  const endY = target.y - ny * (targetSize.h / 2 + 6);
  return `M ${startX} ${startY} L ${endX} ${endY}`;
}

function edgeLabelPos(edge: GraphEdge): { x: number; y: number } {
  const source = nodesById.value.get(edge.sourceId);
  const target = nodesById.value.get(edge.targetId);
  if (typeof source === "undefined" || typeof target === "undefined")
    return { x: 0, y: 0 };
  return {
    x: (source.x + target.x) / 2,
    y: (source.y + target.y) / 2,
  };
}

function onEdgeClick(event: MouseEvent, edge: GraphEdge): void {
  event.stopPropagation();
  if (edge.kind === "arc") {
    void port.selectProjectDatabase(edge.sourceId);
    return;
  }
  selectedEdgeId.value = edge.id;
  openEditEdge(edge);
}

// --- Link create / edit dialogs (writes the source entry's frontmatter) ----

const linkDialogOpen = ref(false);
const editingSourceFile = ref("");
const editingLinkId = ref<Maybe<string>>(nothing);
const linkTarget = ref("");
const linkRelation = ref("related_to");
const linkNote = ref("");
const linkBusy = ref(false);

const linkableTargets = computed(() =>
  port.databaseEntryFields
    .filter((entry) => !entry.isMap && entry.name !== editingSourceFile.value)
    .map((entry) => ({
      value: entry.name,
      label: displayFileName(entry.name),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    ),
);

function openCreateLink(sourceFile: string, targetFile: string): void {
  editingSourceFile.value = sourceFile;
  editingLinkId.value = nothing;
  linkTarget.value = targetFile;
  linkRelation.value = "related_to";
  linkNote.value = "";
  linkDialogOpen.value = true;
}

function openEditEdge(edge: GraphEdge): void {
  editingSourceFile.value = edge.sourceId;
  const linkId = edge.id.slice(edge.sourceId.length + 1);
  editingLinkId.value = just(linkId);
  linkTarget.value = edge.targetId;
  linkRelation.value = edge.relation;
  linkNote.value = matchMaybe(edge.note, {
    just: (note) => note,
    nothing: () => "",
  });
  linkDialogOpen.value = true;
}

function closeLinkDialog(): void {
  linkDialogOpen.value = false;
  editingLinkId.value = nothing;
}

async function commitLink(): Promise<void> {
  const root = fromMaybe(port.rootPath);
  const source = editingSourceFile.value;
  const file = linkTarget.value.trim();
  const relation = linkRelation.value.trim();
  if (
    root === null ||
    root.length === 0 ||
    source.length === 0 ||
    file.length === 0 ||
    relation.length === 0 ||
    linkBusy.value
  )
    return;
  linkBusy.value = true;
  try {
    const fields = await getProjectDatabaseEntryFields(root, source);
    const noteTrimmed = linkNote.value.trim();
    const nextLink: EntityLink = {
      id: matchMaybe(editingLinkId.value, {
        nothing: () => newEntityLinkId(),
        just: (id) => id,
      }),
      file,
      relation,
      note: noteTrimmed.length > 0 ? just(noteTrimmed) : nothing,
    };
    const editingIdM = editingLinkId.value;
    const links = isJust(editingIdM)
      ? fields.links.map((link) =>
          link.id === editingIdM.value ? nextLink : link,
        )
      : [...fields.links, nextLink];
    await updateProjectDatabaseEntryFields(root, source, {
      ...fields,
      links,
    });
    await port.refreshDatabase();
    if (isGraphNodeActive(source)) {
      await port.reloadActiveEntryFields();
    }
    closeLinkDialog();
  } catch (error) {
    console.error("Failed to save relationship from graph", error);
    window.alert(
      error instanceof Error ? error.message : "Could not save relationship.",
    );
  } finally {
    linkBusy.value = false;
  }
}

async function removeLink(): Promise<void> {
  const root = fromMaybe(port.rootPath);
  const source = editingSourceFile.value;
  const linkIdM = editingLinkId.value;
  if (
    root === null ||
    root.length === 0 ||
    source.length === 0 ||
    isNothing(linkIdM) ||
    linkBusy.value
  )
    return;
  const linkId = linkIdM.value;
  linkBusy.value = true;
  try {
    const fields = await getProjectDatabaseEntryFields(root, source);
    await updateProjectDatabaseEntryFields(root, source, {
      ...fields,
      links: fields.links.filter((link) => link.id !== linkId),
    });
    await port.refreshDatabase();
    if (isGraphNodeActive(source)) {
      await port.reloadActiveEntryFields();
    }
    closeLinkDialog();
    selectedEdgeId.value = null;
  } catch (error) {
    console.error("Failed to remove relationship from graph", error);
    window.alert(
      error instanceof Error ? error.message : "Could not remove relationship.",
    );
  } finally {
    linkBusy.value = false;
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape" && linkingFromId.value !== null) {
    linkingFromId.value = null;
    linkCursor.value = null;
  }
}

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  if (persistLayoutTimer.isPending()) {
    persistLayoutTimer.clear();
    persistLayoutNow();
  }
});

const linkingPreview = computed(() => {
  if (
    linkingFromId.value === null ||
    linkingFromId.value.length === 0 ||
    linkCursor.value === null
  )
    return null;
  const source = nodesById.value.get(linkingFromId.value);
  if (typeof source === "undefined") return null;
  return {
    x1: source.x,
    y1: source.y,
    x2: linkCursor.value.x,
    y2: linkCursor.value.y,
  };
});

const emptyState = computed(() => {
  const all = port.databaseEntryFields.filter((e) => !e.isMap);
  if (all.length === 0) return "Create worldbuilding entries to see them here.";
  if (graph.value.nodes.length === 0) return "No entries match these filters.";
  if (graph.value.edges.length === 0)
    return "No relationships yet. Drag from a node's · handle to another, or add relationships from the entry panel.";
  return null;
});
</script>

<template>
  <div class="entity-graph">
    <header
      class="entity-graph-toolbar"
      role="toolbar"
      aria-label="Graph tools"
    >
      <div class="entity-graph-tools">
        <button
          type="button"
          class="entity-graph-tool"
          :class="{ 'entity-graph-tool-active': showArcScenes }"
          :disabled="availableArcCharacters.length === 0"
          :title="
            availableArcCharacters.length > 0
              ? 'Show manuscript scenes linked from character arcs'
              : 'Link arc events to scenes to use this view'
          "
          @click="showArcScenes = showArcScenes === false"
        >
          Scenes
        </button>
        <ZwMenu align="start" :menu-label="nothing">
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="entity-graph-tool"
              :class="{ 'entity-graph-tool-active': hasActiveFilters }"
              v-bind="attrs"
              @click="toggle()"
            >
              <IconFilter class="size-3.5" aria-hidden="true" />
              Filter
            </button>
          </template>
          <div
            v-if="availableTypes.length > 0"
            class="entity-graph-filter-section"
          >
            <p class="entity-graph-filter-heading">Type</p>
            <ZwCheckbox
              v-for="type in availableTypes"
              :key="type"
              class="entity-graph-filter-option"
              :model-value="typeFilters.has(type)"
              @update:model-value="toggleTypeFilter(type)"
            >
              {{ graphTypeFilterLabel(type) }}
            </ZwCheckbox>
          </div>
          <ZwMenuSeparator
            v-if="availableTypes.length > 0 && availableTags.length > 0"
          />
          <div
            v-if="availableTags.length > 0"
            class="entity-graph-filter-section"
          >
            <p class="entity-graph-filter-heading">Tags</p>
            <ZwCheckbox
              v-for="tag in availableTags"
              :key="tag"
              class="entity-graph-filter-option"
              :model-value="tagFilters.has(tag)"
              @update:model-value="toggleTagFilter(tag)"
            >
              {{ tag }}
            </ZwCheckbox>
          </div>
          <ZwMenuSeparator
            v-if="
              showArcScenes &&
              availableArcCharacters.length > 0 &&
              (availableTypes.length > 0 || availableTags.length > 0)
            "
          />
          <div
            v-if="showArcScenes && availableArcCharacters.length > 0"
            class="entity-graph-filter-section"
          >
            <p class="entity-graph-filter-heading">Arc characters</p>
            <ZwCheckbox
              v-for="character in availableArcCharacters"
              :key="character.id"
              class="entity-graph-filter-option"
              :model-value="arcCharacterFilters.has(character.id)"
              @update:model-value="toggleArcCharacterFilter(character.id)"
            >
              {{ character.label }}
            </ZwCheckbox>
          </div>
          <ZwMenuSeparator v-if="hasActiveFilters" />
          <button
            v-if="hasActiveFilters"
            type="button"
            class="entity-graph-filter-clear"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </ZwMenu>
        <button type="button" class="entity-graph-tool" @click="reLayout">
          Re-layout
        </button>
        <button type="button" class="entity-graph-tool" @click="fitView">
          Fit
        </button>
      </div>
      <p class="entity-graph-hint">
        Drag to arrange · drag from · to relate · double-click to open
        <template v-if="showArcScenes"> · diamonds are scenes</template>
      </p>
    </header>

    <div class="entity-graph-stage">
      <p
        v-if="emptyState !== null && graph.nodes.length === 0"
        class="entity-graph-empty"
      >
        {{ emptyState }}
      </p>
      <svg
        ref="svgEl"
        class="entity-graph-svg"
        role="img"
        aria-label="Worldbuilding relationship graph"
        @wheel.prevent="onWheel"
        @pointerdown="onBackgroundPointerDown"
        @pointermove="onBackgroundPointerMove"
        @pointerup="onBackgroundPointerUp"
        @pointercancel="onBackgroundPointerUp"
      >
        <g :transform="transform">
          <line
            v-if="linkingPreview !== null"
            class="entity-graph-link-preview"
            :x1="linkingPreview.x1"
            :y1="linkingPreview.y1"
            :x2="linkingPreview.x2"
            :y2="linkingPreview.y2"
          />

          <g
            v-for="edge in graph.edges"
            :key="edge.id"
            class="entity-graph-edge"
            :class="{
              'entity-graph-edge-selected': edge.id === selectedEdgeId,
              'entity-graph-edge-arc': edge.kind === 'arc',
            }"
            @click="onEdgeClick($event, edge)"
            @pointerdown.stop
          >
            <path
              class="entity-graph-edge-hit"
              :d="edgePath(edge)"
              fill="none"
            />
            <path
              class="entity-graph-edge-line"
              :d="edgePath(edge)"
              fill="none"
              :marker-end="
                edge.kind === 'arc' ? undefined : 'url(#entity-graph-arrow)'
              "
            />
            <text
              class="entity-graph-edge-label"
              :x="edgeLabelPos(edge).x"
              :y="edgeLabelPos(edge).y"
              text-anchor="middle"
              dominant-baseline="middle"
            >
              {{ edge.relationLabel }}
            </text>
          </g>

          <g
            v-for="node in graph.nodes"
            :key="node.id"
            class="entity-graph-node"
            :class="{
              'entity-graph-node-active': isGraphNodeActive(node.id),
              'entity-graph-node-colored':
                node.kind === 'entry' && isJust(node.color),
              'entity-graph-node-scene': node.kind === 'scene',
            }"
            :transform="`translate(${node.x - nodeBounds(node).w / 2} ${node.y - nodeBounds(node).h / 2})`"
            @pointerdown="onNodePointerDown($event, node)"
            @click="onNodeClick($event, node)"
            @dblclick="onNodeDblClick($event, node)"
          >
            <polygon
              v-if="node.kind === 'scene'"
              class="entity-graph-node-scene-body"
              :points="sceneNodePoints(node)"
            />
            <rect
              v-else
              class="entity-graph-node-body"
              :width="nodeBounds(node).w"
              :height="nodeBounds(node).h"
              rx="6"
              ry="6"
              :style="nodeBodyStyle(node)"
            />
            <text
              v-if="node.kind === 'scene'"
              class="entity-graph-node-label entity-graph-node-label-scene"
              text-anchor="middle"
            >
              <tspan
                v-for="(line, lineIndex) in sceneNodeLabelLines(node.label)"
                :key="`${node.id}-${lineIndex}`"
                :x="nodeBounds(node).w / 2"
                :y="sceneLabelLineOffset(node, lineIndex)"
                dominant-baseline="middle"
              >
                {{ line }}
              </tspan>
            </text>
            <text
              v-else
              class="entity-graph-node-label"
              :x="nodeBounds(node).w / 2"
              y="18"
              text-anchor="middle"
              :style="nodeLabelFill(node)"
            >
              {{ node.label }}
            </text>
            <text
              v-if="isJust(node.type)"
              class="entity-graph-node-type"
              :x="nodeBounds(node).w / 2"
              y="36"
              text-anchor="middle"
              :style="nodeTypeFill(node)"
            >
              {{ graphNodeTypeCaption(node.type) }}
            </text>
            <circle
              v-if="node.kind === 'entry'"
              class="entity-graph-link-handle"
              :cx="nodeBounds(node).w - 8"
              :cy="nodeBounds(node).h / 2"
              r="5"
              @pointerdown="onLinkHandlePointerDown($event, node)"
            />
          </g>
        </g>

        <defs>
          <marker
            id="entity-graph-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" class="entity-graph-arrow" />
          </marker>
        </defs>
      </svg>
      <p
        v-if="
          emptyState !== null &&
          graph.nodes.length > 0 &&
          graph.edges.length === 0
        "
        class="entity-graph-banner"
      >
        {{ emptyState }}
      </p>
    </div>

    <EntityRelationshipDialog
      v-model:target-file="linkTarget"
      v-model:relation="linkRelation"
      v-model:note="linkNote"
      :open="linkDialogOpen"
      :source-file="editingSourceFile"
      :editing="isJust(editingLinkId)"
      :busy="linkBusy"
      :linkable-entries="linkableTargets"
      @close="closeLinkDialog"
      @save="commitLink"
      @remove="removeLink"
    />
  </div>
</template>

<style scoped>
.entity-graph {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg);
  font-family: var(--font-ui);
}

.entity-graph-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  min-height: 2.25rem;
  padding: 0.35rem 0.75rem;
  border-bottom: 1px solid var(--line);
  background: var(--bg-raised, var(--bg));
}

.entity-graph-tools {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.entity-graph-tool {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: 1px solid var(--line);
  background: var(--bg);
  color: var(--text-muted);
  border-radius: 0.25rem;
  padding: 0.28rem 0.55rem;
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-out-quart),
    background-color var(--duration-fast) var(--ease-out-quart),
    border-color var(--duration-fast) var(--ease-out-quart);
}

.entity-graph-tool:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.entity-graph-tool-active {
  color: var(--text-strong);
  border-color: color-mix(in oklch, var(--color-zw-amber) 55%, var(--line));
  background: color-mix(in oklch, var(--color-zw-amber) 14%, var(--bg));
}

.entity-graph-hint {
  margin: 0 0 0 auto;
  min-width: 0;
  font-size: 0.75rem;
  line-height: 1.3;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.entity-graph-filter-section {
  padding: 0.35rem 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.entity-graph-filter-heading {
  margin: 0 0 0.15rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.entity-graph-filter-option {
  font-size: 0.88rem;
}

.entity-graph-filter-clear {
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  padding: 0.4rem 0.55rem;
  text-align: left;
  font: inherit;
  font-size: 0.85rem;
  color: var(--text-muted);
  cursor: pointer;
}

.entity-graph-filter-clear:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.entity-graph-stage {
  position: relative;
  flex: 1;
  min-height: 0;
}

.entity-graph-svg {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  cursor: grab;
}

.entity-graph-svg:active {
  cursor: grabbing;
}

.entity-graph-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.95rem;
  pointer-events: none;
}

.entity-graph-banner {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  margin: 0;
  max-width: min(28rem, calc(100% - 2rem));
  padding: 0.45rem 0.75rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised, var(--bg));
  color: var(--text-muted);
  font-size: 0.82rem;
  text-align: center;
  pointer-events: none;
}

.entity-graph-edge-hit {
  stroke: transparent;
  stroke-width: 14;
  cursor: pointer;
}

.entity-graph-edge-line {
  stroke: var(--line);
  stroke-width: 1.5;
  pointer-events: none;
}

.entity-graph-edge-selected .entity-graph-edge-line {
  stroke: var(--color-zw-amber);
}

.entity-graph-edge-arc .entity-graph-edge-line {
  stroke: color-mix(in oklch, var(--color-zw-amber) 55%, var(--line));
  stroke-dasharray: 5 4;
}

.entity-graph-edge-arc .entity-graph-edge-label {
  fill: color-mix(in oklch, var(--color-zw-amber) 75%, var(--text-muted));
}

.entity-graph-edge-label {
  fill: var(--text-muted);
  font-size: 10px;
  pointer-events: none;
}

.entity-graph-arrow {
  fill: var(--line);
}

.entity-graph-edge-selected .entity-graph-arrow {
  fill: var(--color-zw-amber);
}

.entity-graph-link-preview {
  stroke: var(--color-zw-amber);
  stroke-width: 1.5;
  stroke-dasharray: 4 3;
  pointer-events: none;
}

.entity-graph-node {
  cursor: pointer;
}

.entity-graph-node-body {
  fill: var(--bg-raised, var(--bg));
  stroke: var(--line);
  stroke-width: 1.5;
}

.entity-graph-node-active:not(.entity-graph-node-colored)
  .entity-graph-node-body {
  stroke: var(--color-zw-amber);
  stroke-width: 2;
}

.entity-graph-node-scene {
  cursor: grab;
}

.entity-graph-node-scene:active {
  cursor: grabbing;
}

.entity-graph-node-scene-body {
  fill: var(--bg-raised, var(--bg));
  stroke: color-mix(in oklch, var(--color-zw-amber) 45%, var(--line));
  stroke-width: 1.5;
}

.entity-graph-node-scene.entity-graph-node-active
  .entity-graph-node-scene-body {
  stroke: var(--color-zw-amber);
  stroke-width: 2;
}

.entity-graph-node-label-scene {
  font-size: 10px;
  font-weight: 500;
}

.entity-graph-node-label {
  fill: var(--text);
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
}

.entity-graph-node-type {
  fill: var(--text-muted);
  font-size: 9px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  pointer-events: none;
}

.entity-graph-link-handle {
  fill: var(--bg-raised, var(--bg));
  stroke: var(--text-muted);
  stroke-width: 1.25;
  cursor: crosshair;
}

.entity-graph-link-handle:hover {
  stroke: var(--color-zw-amber);
  fill: color-mix(in oklch, var(--color-zw-amber) 18%, var(--bg));
}
</style>
